import { GroupCompanyReportList } from '@shared/api/types';
import Text from '@shared/components/content/text';
import Unit from '@shared/components/content/unit';
import { BadgeCell } from '@shared/components/data-table/cells/badge-cell';
import { HeaderCell } from '@shared/components/data-table/cells/header-cell';
import { NumberCell } from '@shared/components/data-table/cells/number-cell';
import { ColumnDef } from '@tanstack/react-table';

export function useGroupCompanyReportsColumns(): ColumnDef<GroupCompanyReportList>[] {
  return [
    {
      id: 'uuid',
      accessorKey: 'uuid',
      enableHiding: true,
    },
    {
      id: 'organizationName',
      accessorKey: 'organizationName',
      header: ({ column }) => (
        <HeaderCell column={column} name="Organisation" />
      ),
      cell: ({ getValue }) => <Text>{getValue<string>()}</Text>,
    },
    {
      id: 'periodLabel',
      accessorKey: 'periodLabel',
      header: ({ column }) => <HeaderCell column={column} name="Year" />,
      cell: ({ getValue }) => (
        <Text variant="subtle">{getValue<string>()}</Text>
      ),
    },
    {
      id: 'status',
      accessorKey: 'status',
      header: ({ column }) => <HeaderCell column={column} name="Status" />,
      cell: ({ getValue }) => {
        const status = getValue<string>();
        const label = status === 'published' ? 'Published' : 'In progress';
        return <BadgeCell text={label} />;
      },
    },
    {
      id: 'scope1Tonnes',
      accessorKey: 'scope1Tonnes',
      header: ({ column }) => (
        <HeaderCell
          column={column}
          name="Scope 1"
          numberColumn
          unit={<Unit variant="tCO2e" />}
        />
      ),
      cell: ({ getValue }) => <NumberCell number={getValue<number>()} />,
    },
    {
      id: 'scope2Tonnes',
      accessorKey: 'scope2Tonnes',
      header: ({ column }) => (
        <HeaderCell
          column={column}
          name="Scope 2"
          numberColumn
          unit={<Unit variant="tCO2e" />}
        />
      ),
      cell: ({ getValue }) => <NumberCell number={getValue<number>()} />,
    },
    {
      id: 'scope3Tonnes',
      accessorKey: 'scope3Tonnes',
      header: ({ column }) => (
        <HeaderCell
          column={column}
          name="Scope 3"
          numberColumn
          unit={<Unit variant="tCO2e" />}
        />
      ),
      cell: ({ getValue }) => {
        const value = getValue<number | null>();
        if (value === null)
          return <p className="pr-[18px] text-right text-gray-500">-</p>;
        return <NumberCell number={value} />;
      },
    },
    {
      id: 'totalTonnes',
      accessorKey: 'totalTonnes',
      header: ({ column }) => (
        <HeaderCell
          column={column}
          name="Total"
          numberColumn
          unit={<Unit variant="tCO2e" />}
        />
      ),
      cell: ({ getValue }) => <NumberCell number={getValue<number>()} />,
    },
  ];
}
